/**
 * GAS WARS card data contract.
 *
 * This file is the single source of truth for what a card definition may contain.
 * Card JSON in src/data/cards/*.json must satisfy CardDefinition. The effect
 * interpreter (src/engine/effects.ts) must implement EVERY op, trigger, static
 * type, target mode, filter field, condition and amount kind listed here, and
 * nothing else. If a card needs something not expressible here, extend this
 * file first, then the interpreter, then the validator, then the card.
 *
 * Game logic must never branch on a card's name or id.
 */

export type Faction = 'btc' | 'eth' | 'sol' | 'bnb' | 'xrp' | 'neutral';
/** Chains that can produce Gas. 'neutral' is not a chain. */
export type Chain = Exclude<Faction, 'neutral'>;

export type CardType =
  | 'character'
  | 'wallet'
  | 'transaction' // main-phase one-shot
  | 'exploit' // any-time one-shot (instant speed)
  | 'protocol' // persistent, unattached
  | 'upgrade'; // persistent, attached to a friendly character

export type Rarity = 'common' | 'uncommon' | 'rare' | 'genesis';

export type Keyword =
  | 'vamp' // lifelink
  | 'drainerLink' // deathtouch
  | 'ape' // haste
  | 'offChain' // flying
  | 'oracle' // reach
  | 'coldStorage' // hexproof
  | 'hodl' // indestructible
  | 'uptime' // vigilance
  | 'raid' // menace
  | 'slippage' // trample
  | 'frontrun' // first strike
  | 'staked'; // defender (cannot attack)

export const ALL_KEYWORDS: Keyword[] = [
  'vamp', 'drainerLink', 'ape', 'offChain', 'oracle', 'coldStorage',
  'hodl', 'uptime', 'raid', 'slippage', 'frontrun', 'staked',
];

export type Subtype =
  // people
  | 'Founder' | 'Developer' | 'Trader' | 'Degen' | 'Whale' | 'Miner' | 'Validator'
  | 'KOL' | 'Influencer' | 'Hacker' | 'Builder' | 'MarketMaker' | 'Bot' | 'Troll'
  | 'Maxi' | 'Farmer' | 'Rugger' | 'Sniper' | 'Banker' | 'Lawyer' | 'Regulator'
  | 'OG' | 'Army'
  // things
  | 'Meme' | 'Coin' | 'Token' | 'NFT' | 'DAO' | 'Node' | 'DApp' | 'Oracle'
  | 'Mascot' | 'Exchange' | 'Bridge' | 'Contract' | 'Event' | 'Hardware' | 'Status';

/** Gas cost. Faction gas can pay generic; generic cannot pay faction. */
export interface Cost {
  generic?: number;
  btc?: number;
  eth?: number;
  sol?: number;
  bnb?: number;
  xrp?: number;
}

// ---------------------------------------------------------------------------
// Filters, targets, amounts, conditions
// ---------------------------------------------------------------------------

/** Who owns/controls the thing being selected, relative to the ability's controller. */
export type Who = 'friendly' | 'enemy' | 'any';

/** Narrow a set of game objects. All listed fields must match (AND). */
export interface Filter {
  types?: CardType[]; // battlefield permanents / cards
  subtypes?: Subtype[]; // any of
  keywords?: Keyword[]; // has all of
  lacksKeywords?: Keyword[];
  faction?: Faction[]; // any of
  minCost?: number;
  maxCost?: number;
  minAttack?: number;
  maxAttack?: number;
  minHealth?: number;
  maxHealth?: number;
  isToken?: boolean;
  isLegendary?: boolean;
  exhausted?: boolean;
  attacking?: boolean;
  blocking?: boolean;
  damaged?: boolean;
  /** true = exclude the ability's own source object */
  excludeSelf?: boolean;
}

export type TargetType =
  | 'character'
  | 'wallet'
  | 'protocol'
  | 'upgrade'
  | 'permanent' // any non-wallet permanent on the battlefield
  | 'player'
  | 'any' // character or player (classic "any target")
  | 'stackItem' // a transaction/exploit on the stack (for revert)
  | 'cardInDust'
  | 'cardInHand'; // used for discard (opponent chooses unless random)

export type TargetMode =
  | 'choose' // controller picks up to `count` (default 1) legal targets when the ability is put on the stack
  | 'all' // every matching object
  | 'random' // engine picks `count` at random from matching
  | 'self' // the source object itself
  | 'host' // the character an Upgrade is attached to
  | 'controller' // the ability's controller (player)
  | 'opponent' // the ability's controller's opponent (player)
  | 'triggering'; // the object that caused the trigger (e.g. the character that died)

export interface Target {
  type: TargetType;
  who?: Who; // default 'any' for choose/all/random; ignored by self/host/controller/opponent
  mode: TargetMode;
  filter?: Filter;
  count?: number; // for choose/random, default 1
  optional?: boolean; // choose: ability may be played with fewer/no targets
  /**
   * choose only: targets in the same ability that share a slot name are ONE choice.
   * "Target character gets +2/+0 and FRONTRUN" = two effects, one slot "a".
   */
  slot?: string;
}

export type Amount =
  | number
  | { kind: 'count'; who: Who; filter: Filter; times?: number; plus?: number } // permanents on battlefield
  | { kind: 'countDust'; who: Who; filter: Filter; times?: number; plus?: number }
  | { kind: 'handSize'; who: 'controller' | 'opponent' }
  | { kind: 'walletCount'; who: 'controller' | 'opponent'; chain?: Chain }
  | { kind: 'sourceAttack' } // this character's current attack
  | { kind: 'sourceHealth' }
  | { kind: 'x' }; // X paid with generic gas when the card was played (card.cost.x = true)

export type Condition =
  | { kind: 'control'; who: Who; filter: Filter; min?: number; max?: number }
  | { kind: 'integrity'; who: 'controller' | 'opponent'; min?: number; max?: number }
  | { kind: 'turn'; min?: number; max?: number }
  | { kind: 'dust'; who: Who; filter: Filter; min?: number }
  | { kind: 'handSize'; who: 'controller' | 'opponent'; min?: number; max?: number }
  | { kind: 'wallets'; who: 'controller' | 'opponent'; chain?: Chain; min?: number; max?: number }
  | { kind: 'playedCardThisTurn'; who: 'controller'; types?: CardType[]; min?: number }
  | { kind: 'sourceDamaged' }
  | { kind: 'not'; condition: Condition }
  | { kind: 'all'; conditions: Condition[] }
  | { kind: 'any'; conditions: Condition[] };

// ---------------------------------------------------------------------------
// Effects (one-shot)
// ---------------------------------------------------------------------------

export type Duration = 'turn' | 'permanent';

export type Effect =
  | { op: 'damage'; amount: Amount; target: Target }
  | { op: 'destroy'; target: Target }
  | { op: 'draw'; amount: Amount; who: 'controller' | 'opponent' | 'each' }
  | { op: 'discard'; amount: Amount; who: 'controller' | 'opponent'; random?: boolean }
  | { op: 'gainIntegrity'; amount: Amount; who: 'controller' | 'opponent' }
  | { op: 'loseIntegrity'; amount: Amount; who: 'controller' | 'opponent' }
  | { op: 'buff'; attack: number; health: number; target: Target; duration: Duration }
  | { op: 'grantKeyword'; keyword: Keyword; target: Target; duration: Duration }
  | { op: 'removeKeyword'; keyword: Keyword; target: Target; duration: Duration }
  | { op: 'createToken'; token: string; amount: Amount; who?: 'controller' | 'opponent' }
  | { op: 'exhaust'; target: Target }
  | { op: 'ready'; target: Target }
  | { op: 'freeze'; target: Target } // exhaust; does not ready during its controller's next Sync
  | { op: 'bounce'; target: Target } // return to owner's hand (tokens cease to exist)
  | { op: 'recover'; target: Target; to: 'hand' | 'battlefield' } // from Dust; target.type must be cardInDust
  | { op: 'fetchWallet'; chain?: Chain | 'any'; to: 'hand' | 'battlefield'; exhausted?: boolean } // search deck for a basic Wallet, shuffle
  | { op: 'addGas'; chain: Chain | 'any'; amount: Amount } // 'any' = generic-only gas
  | { op: 'revert'; target: Target } // counter a stack item; target.type must be stackItem
  | { op: 'blacklist'; target: Target }
  | { op: 'mill'; amount: Amount; who: 'controller' | 'opponent' }
  | { op: 'sacrifice'; target: Target } // controller chooses own permanent(s) matching filter; counts as death
  | { op: 'scry'; amount: Amount } // look at top N, put any on bottom in chosen order (UI: simple keep/bottom per card)
  | { op: 'conditional'; condition: Condition; then: Effect[]; else?: Effect[] }
  | { op: 'sequence'; effects: Effect[] }; // explicit grouping (rarely needed)

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

export type TriggerEvent =
  | 'self:deploy' // this permanent enters the battlefield
  | 'self:death' // this permanent goes from battlefield to Dust
  | 'self:attack' // this character is declared as an attacker
  | 'self:block' // this character is declared as a blocker
  | 'self:damagePlayer' // this character deals combat damage to a player
  | 'self:survivesDamage' // damaged but not destroyed (after damage step)
  | 'turn:start' // controller's Sync step
  | 'turn:end' // controller's End step
  | 'character:deploy' // any character enters; use filter/who
  | 'character:death' // any character dies; use filter/who
  | 'wallet:install' // controller installs a Wallet
  | 'spell:play' // a transaction or exploit is played; use who/filter.types
  | 'integrity:gain' // controller gains Integrity
  | 'token:create'; // controller creates one or more tokens

export interface TriggeredAbility {
  kind: 'triggered';
  event: TriggerEvent;
  who?: Who; // for character:*, spell:* events. default 'friendly'
  filter?: Filter; // applied to the triggering object
  condition?: Condition; // checked when the trigger would fire
  effects: Effect[];
  oncePerTurn?: boolean;
}

export interface ActivatedAbility {
  kind: 'activated';
  cost: {
    gas?: Cost;
    exhaust?: boolean;
    sacrificeSelf?: boolean;
    sacrifice?: Filter; // sacrifice another friendly permanent matching filter
    discard?: number;
    payIntegrity?: number;
    removeFromDust?: boolean; // activate from the Dust (e.g., recursion); implies the card is in Dust
  };
  speed: 'main' | 'any';
  oncePerTurn?: boolean;
  effects: Effect[];
  label?: string; // short UI label, e.g. "Rug pull"
}

export type StaticEffect =
  | { type: 'buff'; who: Who; filter: Filter; attack: number; health: number } // continuous
  | { type: 'grantKeyword'; who: Who; filter: Filter; keyword: Keyword }
  | { type: 'costModifier'; who: Who; filter: Filter; amount: number } // negative = cheaper; applies to cards in hand
  | { type: 'cantBlock' } // this character cannot block
  | { type: 'cantBeBlockedBy'; filter: Filter } // e.g. can't be blocked by Tokens
  | { type: 'walletsProduceExtra'; chain: Chain; amount: number } // controller's Wallets of chain produce +N
  | { type: 'extraWalletInstall'; amount: number } // controller may install N extra Wallets per turn
  | { type: 'handSizeModifier'; amount: number }
  | { type: 'damageModifier'; who: 'friendly' | 'enemy'; filter: Filter; amount: number } // characters deal +N damage
  | { type: 'opponentCharactersEnterExhausted' }
  | { type: 'attackRequirement'; filter: Filter }; // matching enemy characters must attack each combat if able

export interface StaticAbility {
  kind: 'static';
  effect: StaticEffect;
  condition?: Condition; // only active while true
}

export type Ability = TriggeredAbility | ActivatedAbility | StaticAbility;

// ---------------------------------------------------------------------------
// Card definition
// ---------------------------------------------------------------------------

export type AiRole =
  | 'aggro' | 'midrange' | 'control' | 'ramp' | 'removal' | 'draw'
  | 'protection' | 'finisher' | 'utility' | 'wallet' | 'sweeper' | 'revert' | 'token';

export interface AiHints {
  role: AiRole;
  /** 1 to 10, how good the card is in a vacuum at its cost. */
  value: number;
  /** 1 to 10, how badly the opponent wants it removed. Characters only meaningful. */
  removalPriority: number;
  /** Optional sequencing hint for bots. */
  playWhen?: 'asap' | 'onCurve' | 'hasTarget' | 'behind' | 'ahead' | 'lethal' | 'response';
}

export interface ArtRef {
  /** Relative to /public, e.g. art/gen/btc-001.svg. Placeholder art is generated; final art replaces the file. */
  path: string;
  artist: string; // "placeholder" until final
  status: 'placeholder' | 'sketch' | 'final';
}

export interface Persona {
  /** Which real-world archetype this fictionalizes, e.g. "exchange-founder". Never a real name. */
  archetypeOf: string;
  licensed: boolean;
  notes?: string;
}

export interface CardDefinition {
  id: string; // gen-btc-001
  name: string;
  set: string; // GEN
  collectorNumber: number;
  rarity: Rarity;
  faction: Faction;
  type: CardType;
  subtypes: Subtype[];
  cost: Cost & { x?: boolean }; // Wallets have cost {}
  attack?: number; // characters
  health?: number; // characters
  keywords: Keyword[];
  abilities: Ability[];
  /** Player-facing rules text. Authoritative for humans; abilities are authoritative for the engine. */
  text: string;
  flavor?: string;
  art: ArtRef;
  legendary?: boolean;
  /** Wallets only. */
  wallet?: { chain: Chain | 'any'; basic: boolean; entersExhausted?: boolean; produces?: number };
  /** Upgrades only: what the host gains. Additional host effects go in abilities with target mode 'host'. */
  upgrade?: { attack: number; health: number; keywords: Keyword[] };
  /** Token definitions this card can create, by token id (defined in tokens.json). */
  tokens?: string[];
  ai: AiHints;
  tags: string[];
  legality: Record<string, 'legal' | 'banned' | 'restricted'>; // format -> status
  persona?: Persona;
}

/** Tokens are Characters with no cost, created by effects. */
export interface TokenDefinition {
  id: string; // tok-shitcoin
  name: string;
  faction: Faction;
  subtypes: Subtype[];
  attack: number;
  health: number;
  keywords: Keyword[];
  abilities: Ability[];
  text: string;
  art: ArtRef;
}

export interface DeckDefinition {
  id: string;
  name: string;
  description?: string;
  factions: Faction[];
  archetype: string; // free text tag used by bots: aggro | midrange | control | ramp | tempo | swarm | dev
  cards: { id: string; count: number }[];
  dev?: boolean; // developer/test deck, hidden from normal play unless dev mode
}
